/**
 * @file Routes publiques (parcours établissement par slug).
 * @module apps.public.routes.Public
 *
 * Les patterns d'URL ne sont pas redéfinis ici : ils proviennent de la source
 * unique `paths.company` (packages/common/routes/paths). Ce fichier associe
 * chaque pattern à sa page et regroupe les pages du site établissement sous la
 * route layout `PublicLayout` (Header + contenu + Footer rendus une seule
 * fois) — les pages ne composent plus le squelette elles-mêmes.
 *
 * Restent hors layout : l'accueil générique Brydge (WelcomePage) et le flux de
 * réservation (ReservationPage), qui est une expérience plein écran sans le
 * header du site.
 */

import React, {lazy} from 'react';
import CompanyValidator from '../components/CompanyValidator';
import PublicLayout from 'packages/common/layouts/public/PublicLayout';
import {paths} from 'packages/common/routes/paths';

// ----------------------------------------------------------------------

const WelcomePage = lazy(() => import('apps/public/pages/welcome/view/WelcomePage'));
const GiftVouchersListPage = lazy(() => import('apps/public/pages/gift-voucher/catalog/view/GiftVouchersListPage'));
const CustomVoucherBuilderPage = lazy(() => import('apps/public/pages/gift-voucher/custom-voucher/view/CustomVoucherBuilderPage'));
const PurchasePage = lazy(() => import('apps/public/pages/gift-voucher/purchase/view/PurchasePage'));
const PaymentResultPage = lazy(() => import('apps/public/pages/gift-voucher/purchase/view/PaymentResultPage'));
const ConfirmationPage = lazy(() => import('apps/public/pages/gift-voucher/purchase/view/ConfirmationPage'));
const ReservationPage = lazy(() => import('apps/public/pages/restaurant/reservation/view/ReservationPage'));
const ReservationConfirmedPage = lazy(() => import('apps/public/pages/restaurant/manage/view/ReservationConfirmedPage'));
const ReservationManagementPage = lazy(() => import('apps/public/pages/restaurant/manage/view/ReservationManagementPage'));
const PaymentUpdatePage = lazy(() => import('apps/public/pages/restaurant/manage/view/PaymentUpdatePage'));
const CardHoldRequestPage = lazy(() => import('apps/public/pages/restaurant/manage/view/CardHoldRequestPage'));
const ReviewPage = lazy(() => import('apps/public/pages/restaurant/review/view/ReviewPage'));
const TermsPage = lazy(() => import('apps/public/pages/gift-voucher/terms/view/TermsPage'));
const ReservationTermsPage = lazy(() => import('apps/public/pages/restaurant/terms/view/ReservationTermsPage'));
const PrivacyPolicyPage = lazy(() => import('apps/public/pages/legal/view/PrivacyPolicyPage'));
const NewsletterUnsubscribePage = lazy(() => import('apps/public/pages/newsletter/view/NewsletterUnsubscribePage'));

// ----------------------------------------------------------------------

// `paths.company` expose des patterns absolus (/:slug/...). Les routes enfants
// de React Router sont relatives au parent '/', d'où le retrait du slash initial.
const rel = (pattern) => pattern.replace(/^\//, '');

// Enveloppe un élément dans le validateur d'établissement (404 si le slug n'existe pas).
const validated = (element) => <CompanyValidator>{element}</CompanyValidator>;

export const publicRoutes = [
  {
    path: '/',
    children: [
      {index: true, element: <WelcomePage />}, // Accueil générique Brydge

      // Site établissement : layout public (Header + Footer), slug validé en amont
      {
        element: validated(<PublicLayout />),
        children: [
          {path: rel(paths.company.home), element: <GiftVouchersListPage />}, // Accueil établissement
          {path: rel(paths.company.giftVoucherList), element: <GiftVouchersListPage />}, // Liste des bons cadeaux
          {path: rel(paths.company.createCustomVoucher), element: <CustomVoucherBuilderPage />}, // Bon sur-mesure
          {path: rel(paths.company.purchase), element: <PurchasePage />}, // Achat de bon cadeau
          {path: rel(paths.company.paymentResult), element: <PaymentResultPage />}, // Retour de paiement
          {path: rel(paths.company.confirmation), element: <ConfirmationPage />}, // Confirmation d'achat
          {path: rel(paths.company.reservationConfirmed), element: <ReservationConfirmedPage />}, // Confirmation de réservation
          {path: rel(paths.company.reservationManage), element: <ReservationManagementPage />}, // Gestion d'une réservation
          {path: rel(paths.company.reservationPayment), element: <PaymentUpdatePage />}, // MAJ paiement (lien email)
          {path: rel(paths.company.reservationCardHold), element: <CardHoldRequestPage />}, // Empreinte bancaire (lien email)
          {path: rel(paths.company.review), element: <ReviewPage />}, // Avis client
          {path: rel(paths.company.terms), element: <TermsPage />}, // CGV établissement
        ],
      },

      // Contenus génériques Brydge : layout public sans validation du slug
      {
        element: <PublicLayout />,
        children: [
          {path: rel(paths.company.reservationTerms), element: <ReservationTermsPage />}, // CGV réservations Brydge
          {path: rel(paths.company.privacyPolicy), element: <PrivacyPolicyPage />}, // Politique de confidentialité Brydge
          {path: rel(paths.company.newsletterUnsubscribe), element: <NewsletterUnsubscribePage />}, // Désinscription newsletter (lien email)
        ],
      },

      // Flux de réservation : plein écran, sans le layout du site
      {path: rel(paths.company.reservation), element: validated(<ReservationPage />)},
    ],
  },
];
